import { useCallback, useEffect, useState } from 'react';
import { CapturaDeFoto } from './CapturaDeFoto';
import { gabaritoDoMetodo } from './gabaritos';
import { mensagemDoErro } from './api';
import {
  desconsiderarEvidenciaDoDiario, desconsiderarEvidenciaDoEnsaio,
  enviarEvidenciasDoDiario, enviarEvidenciasDoEnsaio,
  verEvidenciasDoDiario, verEvidenciasDoEnsaio,
  type FotoDeEnsaio, type TipoDeFoto,
} from './api/evidencias';
import { Aviso, Dialogo, Selo } from './ui';

/**
 * O bloco de fotos de um registro técnico — o mesmo em toda tela.
 *
 * ─── Por que um componente, e não um bloco por tela ──────────────────
 *
 * Porque fotografar corpo de prova é o mesmo ofício em toda bancada, e
 * porque as duas telas que já tinham fotografia — adensamento e módulo
 * de resiliência — escreveram o bloco duas vezes e já divergiam na
 * terceira semana: uma mostrava o que faltava, a outra não; uma deixava
 * desconsiderar, a outra só pela API.
 *
 * ─── O gabarito vem do MÉTODO ────────────────────────────────────────
 *
 * «Para outros ensaios, como triaxial, cisalhamento direto, terão outros
 *  gabaritos com as formas correspondentes de cada CP» — Humberto.
 *
 * Quem escolhe a forma é o código do método, e não esta tela: o mesmo
 * ensaio lançado por outra tela continua sendo o mesmo corpo de prova.
 * Método sem gabarito cadastrado cai no quadro livre — pior que o
 * gabarito certo, melhor que o gabarito errado.
 *
 * ─── Roteiro, quando o laudo espera fotos certas ─────────────────────
 *
 * `roteiro` é a lista do que o laudo daquele ensaio espera ver. Onde ele
 * existe, o bloco diz o que falta com a mesma clareza das pendências —
 * um laudo com folha fotográfica vazia não avisa que está vazia. Onde
 * não existe, sobra um botão só: anexar o que a fotografia tiver a
 * dizer.
 */

export interface ItemDoRoteiro {
  tipo: TipoDeFoto;
  nome: string;
  /** Em que momento da bancada se tira esta foto. */
  quando?: string;
}

const ROTULO: Record<TipoDeFoto, string> = {
  cp_antes: 'CP antes',
  cp_depois: 'CP depois',
  moldagem: 'Moldagem',
  montagem: 'Montagem',
  ruptura: 'Ruptura',
  equipamento: 'Equipamento',
  outro: 'Outra',
};

export function FotosDoRegistro({
  id, alvo = 'ensaio', metodo, numero, titulo = 'Fotos do ensaio', dica,
  roteiro = [],
  podeLancar = true,
}: {
  id: string;
  /**
   * De quem é a fotografia. Padrão: o ensaio.
   *
   * O dia de campo entra pelas rotas de `/diarios/:id/evidencias`
   * (`0138`) — a fotografia técnica é a mesma coisa nos dois casos, e o
   * que muda é de quem ela é.
   */
  alvo?: 'ensaio' | 'diario';
  /**
   * O código do método, quando a tela já o tem. É ele que escolhe o
   * gabarito — mas quem manda é o que a API responde: um código escrito
   * à mão na tela continua ali no dia em que o método for cadastrado
   * com outro.
   */
  metodo?: string | null;
  /** O número do bloco na sequência da tela, quando a tela numera. */
  numero?: number;
  titulo?: string;
  dica?: string;
  roteiro?: ItemDoRoteiro[];
  podeLancar?: boolean;
}) {
  const [fotos, setFotos] = useState<FotoDeEnsaio[] | null>(null);
  const [metodoDaApi, setMetodoDaApi] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [descartando, setDescartando] = useState<FotoDeEnsaio | null>(null);
  const [motivo, setMotivo] = useState('');

  const doDiario = alvo === 'diario';

  const carregar = useCallback(async () => {
    try {
      const r = doDiario
        ? await verEvidenciasDoDiario(id) : await verEvidenciasDoEnsaio(id);
      setFotos(r.fotos); setMetodoDaApi(r.metodo_codigo ?? null);
    } catch (e) { setErro(mensagemDoErro(e)); }
  }, [id, doDiario]);

  useEffect(() => { void carregar(); }, [carregar]);

  const valem = (fotos ?? []).filter((f) => !f.desconsiderada);
  const tem = (t: TipoDeFoto) => valem.some((f) => f.tipo === t);

  const enviar = async (tipo: TipoDeFoto, arquivo: File) => {
    setOcupado(true); setErro(null);
    try {
      const r = doDiario
        ? await enviarEvidenciasDoDiario(id, [{ arquivo, tipo }])
        : await enviarEvidenciasDoEnsaio(id, [{ arquivo, tipo }]);
      setFotos(r.fotos); setMetodoDaApi(r.metodo_codigo ?? null);
    } catch (e) { setErro(mensagemDoErro(e)); } finally { setOcupado(false); }
  };

  const descartar = async () => {
    if (!descartando) return;
    setOcupado(true); setErro(null);
    try {
      const r = doDiario
        ? await desconsiderarEvidenciaDoDiario(id, descartando.id, motivo)
        : await desconsiderarEvidenciaDoEnsaio(id, descartando.id, motivo);
      setFotos(r.fotos); setMetodoDaApi(r.metodo_codigo ?? null);
      setDescartando(null); setMotivo('');
    } catch (e) { setErro(mensagemDoErro(e)); } finally { setOcupado(false); }
  };

  /* O método vem da API; a prop só serve para a tela que já o tem em
     mãos e quer evitar o piscar do primeiro quadro sem gabarito. */
  const gabarito = gabaritoDoMetodo(metodoDaApi ?? metodo);
  const faltando = roteiro.filter((r) => !tem(r.tipo));

  return (
    <section className="bloco">
      <h2>
        {numero != null ? `${numero} · ` : ''}{titulo}
        <span className="estado-do-bloco">
          {fotos == null ? 'carregando'
            : valem.length === 0 ? 'sem foto'
              : `${valem.length} foto${valem.length === 1 ? '' : 's'}`}
          {fotos != null && valem.length > 0 && faltando.length > 0
            && ` · falta ${faltando.map((f) => f.nome).join(', ')}`}
        </span>
      </h2>
      {dica && <p className="sub">{dica}</p>}

      {erro && <Aviso tipo="erro">{erro}</Aviso>}

      {podeLancar && (
        /*
          `div`, e não `label`, em cada item: um clique em qualquer lugar
          de um `label` dispara o `<input type="file">` que estiver dentro
          dele — e a captura carrega dois, escondidos. A pessoa tocaria no
          nome da foto e a galeria abriria sozinha.
        */
        <div className="galeria-envio">
          {roteiro.map((p) => (
            <div className="campo" key={p.tipo}>
              <span className="rotulo">
                {p.nome}{p.quando ? <span className="sub"> ({p.quando})</span> : null}{' '}
                {tem(p.tipo)
                  ? <Selo tom="ok">Anexada</Selo>
                  : <Selo tom="atencao">Falta</Selo>}
              </span>
              <CapturaDeFoto
                tipo={p.tipo}
                titulo={p.nome}
                gabarito={gabarito}
                desabilitado={ocupado}
                aoReceber={(arquivo) => enviar(p.tipo, arquivo)}
              />
            </div>
          ))}
          <div className="campo">
            <span className="rotulo">
              Outra foto <span className="sub">(o que a foto tiver a dizer)</span>
            </span>
            <CapturaDeFoto
              tipo="outro"
              titulo="Foto do ensaio"
              gabarito={gabarito}
              desabilitado={ocupado}
              aoReceber={(arquivo) => enviar('outro', arquivo)}
            />
          </div>
        </div>
      )}

      {fotos != null && fotos.length > 0 && (
        <ul className="lista-fotos">
          {fotos.map((f) => (
            <li key={f.id} className={f.desconsiderada ? 'descartada' : undefined}>
              <span className="mono">{ROTULO[f.tipo] ?? f.tipo}</span>
              {f.legenda ? ` — ${f.legenda}` : ''}
              {f.criado_por_nome ? <span className="sub"> · {f.criado_por_nome}</span> : null}
              {f.desconsiderada
                ? <span className="sub"> · desconsiderada: {f.motivo_desconsideracao}</span>
                : podeLancar && (
                  <button
                    type="button" className="ligacao"
                    disabled={ocupado}
                    onClick={() => { setDescartando(f); setMotivo(''); }}
                  >
                    desconsiderar
                  </button>
                )}
            </li>
          ))}
        </ul>
      )}

      <Dialogo
        aberto={descartando != null}
        titulo="Desconsiderar a foto"
        aoFechar={() => setDescartando(null)}
      >
        <p className="sub">
          A foto não é apagada: ela sai do laudo e fica registrada com o
          motivo. Evidência que some não é evidência.
        </p>
        <label className="campo">
          <span className="rotulo">Por quê?</span>
          <textarea
            value={motivo} rows={3}
            onChange={(ev) => setMotivo(ev.target.value)}
            placeholder="tremida, enquadramento errado, duplicada…"
          />
        </label>
        <div className="acoes">
          <button type="button" onClick={() => setDescartando(null)}>
            Cancelar
          </button>
          <button
            type="button" className="principal"
            disabled={ocupado || motivo.trim().length < 5}
            onClick={() => { void descartar(); }}
          >
            {ocupado ? 'Gravando…' : 'Desconsiderar'}
          </button>
        </div>
      </Dialogo>
    </section>
  );
}
